export const DEFAULT_PRICING_SETTINGS = {
  version: 1,
  shared: {
    minNetHuf: 20000,
    missingDistanceFallbackNetHuf: 20000,
    volumeThresholdM3: 1,
    insuranceMinNetHuf: 6000,
    insuranceRatePercent: 1,
    weightBands: [
      { label: '0-20 kg', maxKg: 20 },
      { label: '20,01-30 kg', maxKg: 30 },
      { label: '30,01-40 kg', maxKg: 40 },
      { label: '40,01-50 kg', maxKg: 50 },
      { label: '50 kg felett', maxKg: null },
    ],
    ratesUnder1M3: [120, 140, 150, 160, 170],
    ratesOver1M3: [150, 160, 170, 180, 200],
  },
  ikea: {
    zones: [
      { value: 'budapest', label: 'Budapest és környéke (+15 km belül)', customQuote: false },
      { value: 'zone70', label: '1. zóna 70 km', customQuote: false },
      { value: 'zone100', label: '2. Zóna 100 km', customQuote: false },
      { value: 'zone150', label: '3. Zóna 150 km', customQuote: false },
      { value: 'zone250', label: '4. zóna 250 km', customQuote: false },
      { value: 'zone250_plus', label: '5. zóna > 250 km', customQuote: true },
    ],
    weightBands: [
      { label: '0-30 kg', maxKg: 30 },
      { label: '30,01-80 kg', maxKg: 80 },
      { label: '80,01-200 kg', maxKg: 200 },
    ],
    zoneWeightPrices: {
      budapest: [6500, 11000, 18500],
      zone70: [11000, 15000, 24000],
      zone100: [14000, 18500, 26000],
      zone150: [19500, 25000, 34000],
      zone250: [28000, 31500, 45000],
    },
    customAboveKg: 200,
    budaorsStoreKey: 'ikea_budaors',
    budaorsZoneKey: 'budapest',
    budaorsSurchargeHuf: 2500,
    valueSurcharges: [
      { value: '0-100', label: '1-100.000 Ft: 0', fee: 0 },
      { value: '100-200', label: '100.001-200.000 Ft: 6000', fee: 6000 },
      { value: '201-400', label: '201.000-400.000 Ft: 9000', fee: 9000 },
      { value: '401+', label: '401.000 Ft-tól: 15000', fee: 15000 },
    ],
    collectionFees: [
      { value: 'none', label: 'Nem igényel: 0', fee: 0 },
      { value: '1-3', label: '1 – 3 tétel: 2500', fee: 2500 },
      { value: '4-6', label: '4 – 6 tétel: 3500', fee: 3500 },
      { value: '7-10', label: '7 – 10 tétel: 4700', fee: 4700 },
    ],
  },
  quickCalculator: {
    ratePerKmHuf: 160,
    minNetHuf: 20000,
  },
};

/** Irányár: nettó minimum, ha (Ft/km × km) ennél kevesebb (táblázat szerint). */
export const ESTIMATE_MIN_NET_HUF = DEFAULT_PRICING_SETTINGS.shared.minNetHuf;

function mergeArrayByIndex(raw, fallback) {
  const arr = Array.isArray(raw) ? raw : [];
  return fallback.map((x, i) => {
    if (x && typeof x === 'object' && !Array.isArray(x)) return { ...x, ...(arr[i] || {}) };
    return arr[i] ?? x;
  });
}

export function normalizePricingSettings(settings) {
  const src = settings && typeof settings === 'object' ? settings : {};
  const shared = src.shared && typeof src.shared === 'object' ? src.shared : {};
  const ikea = src.ikea && typeof src.ikea === 'object' ? src.ikea : {};
  const quick = src.quickCalculator && typeof src.quickCalculator === 'object' ? src.quickCalculator : {};
  const defaults = DEFAULT_PRICING_SETTINGS;
  const zones = mergeArrayByIndex(ikea.zones, defaults.ikea.zones);
  const zoneWeightPrices = { ...defaults.ikea.zoneWeightPrices };
  Object.keys(zoneWeightPrices).forEach((zoneKey) => {
    zoneWeightPrices[zoneKey] = mergeArrayByIndex(ikea.zoneWeightPrices?.[zoneKey], zoneWeightPrices[zoneKey]).map((n) =>
      Math.max(0, Math.round(Number(n) || 0)),
    );
  });
  return {
    version: 1,
    shared: {
      ...defaults.shared,
      ...shared,
      insuranceMinNetHuf: Math.max(0, Math.round(Number(shared.insuranceMinNetHuf ?? defaults.shared.insuranceMinNetHuf) || 0)),
      insuranceRatePercent: Math.max(0, Number(shared.insuranceRatePercent ?? defaults.shared.insuranceRatePercent) || 0),
      weightBands: mergeArrayByIndex(shared.weightBands, defaults.shared.weightBands),
      ratesUnder1M3: mergeArrayByIndex(shared.ratesUnder1M3, defaults.shared.ratesUnder1M3).map((n) =>
        Math.max(0, Math.round(Number(n) || 0)),
      ),
      ratesOver1M3: mergeArrayByIndex(shared.ratesOver1M3, defaults.shared.ratesOver1M3).map((n) =>
        Math.max(0, Math.round(Number(n) || 0)),
      ),
    },
    ikea: {
      ...defaults.ikea,
      ...ikea,
      zones,
      weightBands: mergeArrayByIndex(ikea.weightBands, defaults.ikea.weightBands),
      zoneWeightPrices,
      valueSurcharges: mergeArrayByIndex(ikea.valueSurcharges, defaults.ikea.valueSurcharges),
      collectionFees: mergeArrayByIndex(ikea.collectionFees, defaults.ikea.collectionFees),
    },
    quickCalculator: {
      ...defaults.quickCalculator,
      ...quick,
    },
  };
}

export function pricingSnapshot(settings) {
  return normalizePricingSettings(settings);
}

export function toPositiveNumber(value) {
  const n = Number(String(value || '').replace(',', '.').trim());
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function estimateWeightColumnIndex(weightKg, sharedSettings = DEFAULT_PRICING_SETTINGS.shared) {
  const w = Number(weightKg);
  const x = Number.isFinite(w) && w > 0 ? w : 0;
  const bands = Array.isArray(sharedSettings.weightBands)
    ? sharedSettings.weightBands
    : DEFAULT_PRICING_SETTINGS.shared.weightBands;
  const idx = bands.findIndex((band) => band.maxKg == null || x <= Number(band.maxKg));
  return idx >= 0 ? idx : bands.length - 1;
}

export function volumeM3FromBoxCm(lengthCm, widthCm, heightCm) {
  const L = toPositiveNumber(lengthCm);
  const W = toPositiveNumber(widthCm);
  const H = toPositiveNumber(heightCm);
  if (L <= 0 || W <= 0 || H <= 0) return 0;
  return (L * W * H) / 1_000_000;
}

export function rateFtPerKm(weightKg, volM3, sharedSettings = DEFAULT_PRICING_SETTINGS.shared) {
  const shared = { ...DEFAULT_PRICING_SETTINGS.shared, ...(sharedSettings || {}) };
  const col = estimateWeightColumnIndex(weightKg, shared);
  const threshold = Number(shared.volumeThresholdM3) > 0 ? Number(shared.volumeThresholdM3) : 1;
  const list = volM3 >= threshold ? shared.ratesOver1M3 : shared.ratesUnder1M3;
  return list[col];
}

/**
 * @param {number} weightKg
 * @param {number} volumeM3 – dobozból vagy összesített m³ mezőből
 * @param {number} distanceKm – kerekített egész km
 */
export function computeNetEstimateFromRates(weightKg, volumeM3, distanceKm, sharedSettings) {
  const shared = { ...DEFAULT_PRICING_SETTINGS.shared, ...(sharedSettings || {}) };
  const d = Math.max(0, Math.round(Number(distanceKm) || 0));
  const ratePerKm = rateFtPerKm(weightKg, volumeM3, shared);
  const rawNetFromKm = d > 0 ? Math.round(ratePerKm * d) : 0;
  const minNet = Math.max(0, Math.round(Number(shared.minNetHuf) || 0));
  const useMinimumFee = d > 0 && rawNetFromKm < minNet;
  const estimateNet = d > 0 ? Math.max(minNet, rawNetFromKm) : 0;
  return { rawNetFromKm, useMinimumFee, estimateNet, ratePerKm, distanceKm: d };
}

export function computeQuickCalculatorEstimate(distanceKm, quickSettings) {
  const quick = { ...DEFAULT_PRICING_SETTINGS.quickCalculator, ...(quickSettings || {}) };
  const km = Math.max(0, Math.round(Number(distanceKm) || 0));
  const rawNet = km * Math.max(0, Math.round(Number(quick.ratePerKmHuf) || 0));
  const estimateNet = km > 0 ? Math.max(Math.max(0, Math.round(Number(quick.minNetHuf) || 0)), rawNet) : 0;
  return { km, rawNet, estimateNet };
}

export function computeInsuranceFeeFromValue(shipmentValue, sharedSettings) {
  const shared = { ...DEFAULT_PRICING_SETTINGS.shared, ...(sharedSettings || {}) };
  const productValue = Math.max(0, Math.round(Number(String(shipmentValue || '').replace(/[^\d]/g, '')) || 0));
  const minFee = Math.max(0, Math.round(Number(shared.insuranceMinNetHuf) || 0));
  const ratePercent = Math.max(0, Number(shared.insuranceRatePercent) || 0);
  const percentFee = Math.ceil((productValue * ratePercent) / 100);
  return Math.max(minFee, percentFee);
}

export function computeIkeaEstimate({
  zone,
  weightKg,
  valueSurchargeValue,
  collectionValue,
  store,
  settings,
}) {
  const pricing = normalizePricingSettings(settings).ikea;
  const weight = toPositiveNumber(weightKg);
  const zoneRow = pricing.zones.find((z) => z.value === zone);
  let delivery = { fee: 0, label: '', custom: false };
  if (zoneRow?.customQuote) {
    delivery = { fee: 0, label: `${zoneRow.label}: egyedi ajánlat`, custom: true };
  } else if (zone && weight > Number(pricing.customAboveKg || 0)) {
    delivery = { fee: 0, label: '200,01 kg fölött: egyedi ajánlat', custom: true };
  } else if (zone && weight > 0) {
    const bandIndex = pricing.weightBands.findIndex((band) => band.maxKg == null || weight <= Number(band.maxKg));
    const row = bandIndex >= 0 ? pricing.weightBands[bandIndex] : null;
    const fee = pricing.zoneWeightPrices?.[zone]?.[bandIndex] ?? 0;
    delivery = row ? { fee: Math.round(Number(fee) || 0), label: row.label, custom: false } : delivery;
  }
  const valueSurcharge = pricing.valueSurcharges.find((x) => x.value === valueSurchargeValue)?.fee ?? 0;
  const collection = pricing.collectionFees.find((x) => x.value === collectionValue)?.fee ?? 0;
  const budaors =
    zone === pricing.budaorsZoneKey && store === pricing.budaorsStoreKey
      ? Math.max(0, Math.round(Number(pricing.budaorsSurchargeHuf) || 0))
      : 0;
  const total = delivery.custom ? valueSurcharge + collection + budaors : delivery.fee + valueSurcharge + collection + budaors;
  return { delivery, valueSurcharge, collection, budaors, total };
}

export function formatEstimateFt(value) {
  return `${Math.round(Number(value) || 0).toLocaleString('hu-HU')} Ft`;
}

export function formatEstimateM3(v) {
  const x = Number(v);
  return x > 0
    ? `${x.toLocaleString('hu-HU', { minimumFractionDigits: 2, maximumFractionDigits: 3 })} m³`
    : '—';
}
